'use client';

import React, { useContext, useState } from "react";
import {
  Navbar,
  Typography,
  IconButton,
  Collapse,
  Button,
} from "@material-tailwind/react";
import Link from "next/link";
import Image from "next/image";
import { FaUserCircle, FaChevronDown } from "react-icons/fa";
import myContext from "@/context/myContext";
import { LoginModal } from "./LoginModal";

const rolePaths = {
  3: '/admin/home',
  4: '/blog-dashboard/home',
  5: '/property-dashboard/home',
  2: '/user/home',
};

// Updated navigation items with Services dropdown
const navigationItems = [
  { href: "/", label: "Home" },
  { href: "/about", label: "About" },
  {
    label: "Services",
    subItems: [
      { href: "/services/packers-&-movers", label: "Packers & Movers" },
      {
        href: "https://www.garvikstay.com/",
        label: "Garvik Stay",
        external: true
      },
      {
        href: "https://www.kamallawfirm.in/",
        label: "Kamal Law Firm",
        external: true
      },
      {
        href: "https://www.kamaldocumentation.com/",
        label: "Kamal Documentation",
        external: true
      },
      // Add more services here as needed
      // { href: "/services/service2", label: "Service 2" },
    ]
  },

  { href: "/blogs", label: "Blogs" },
  { href: "/contact", label: "Contact" },
];

export default function Navbars() {
  const [openNav, setOpenNav] = useState(false);
  const [openMobileServices, setOpenMobileServices] = useState(false);
  const { user } = useContext(myContext);
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 960) {
        setOpenNav(false);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLinkClick = (e, item) => {
    if (item.requiresLogin && !user) {
      e.preventDefault();
      setLoginModalOpen(true);
    }
  };

  // Desktop navigation with dropdown
  const navList = (
    <div className="mb-4 mt-2 font-bold text-gray-700 flex flex-col gap-2 lg:mb-0 lg:mt-0 lg:flex-row lg:items-center lg:gap-2">
      {navigationItems.map((item, index) => (
        item.subItems ? (
          <div key={index} className="relative group">
            <Typography
              as="div"
              color="blue-gray"
              className="p-1 text-md app-font cursor-pointer flex items-center gap-1"
            >
              {item.label}
              <FaChevronDown className="text-xs transition-transform group-hover:rotate-180" />
            </Typography>
            <div className="absolute top-full left-0 w-48 bg-white shadow-lg rounded-md z-50 hidden group-hover:block">
              {item.subItems.map((subItem, subIndex) => (
                <Typography
                  key={subIndex}
                  as="div"
                  color="blue-gray"
                  className="p-1 app-font hover:bg-gray-50"
                >
                  <Link
                    href={subItem.href}
                    className="flex items-center px-4 py-2"
                    onClick={(e) => handleLinkClick(e, subItem)}
                  >
                    {subItem.label}
                  </Link>
                </Typography>
              ))}
            </div>
          </div>
        ) : (
          <Typography
            key={index}
            as="div"
            color="blue-gray"
            className="p-1 text-md app-font"
          >
            {item.external ? (
              <a
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center"
              >
                {item.label}
              </a>
            ) : (
              <Link
                href={item.href}
                className="flex items-center"
                onClick={(e) => handleLinkClick(e, item)}
              >
                {item.label}
              </Link>
            )}
          </Typography>
        )
      ))}
    </div>
  );

  // Mobile navigation with collapsible services
  const mobileNavList = (
    <div className="mb-4 mt-2 flex flex-col gap-2 lg:mb-0 lg:mt-0">
      {navigationItems.map((item, index) => (
        item.subItems ? (
          <div key={index}>
            <Typography
              as="div"
              variant="small"
              color="blue-gray"
              className="p-1 app-font flex items-center justify-between cursor-pointer"
              onClick={() => setOpenMobileServices(!openMobileServices)}
            >
              <span>{item.label}</span>
              <FaChevronDown
                className={`text-xs transition-transform ${openMobileServices ? 'rotate-180' : ''}`}
              />
            </Typography>
            <Collapse open={openMobileServices}>
              <div className="pl-4">
                {item.subItems.map((subItem, subIndex) => (
                  <Typography
                    key={subIndex}
                    as="div"
                    variant="small"
                    color="blue-gray"
                    className="p-1 app-font"
                  >
                    <Link
                      href={subItem.href}
                      className="flex items-center"
                      onClick={(e) => handleLinkClick(e, subItem)}
                    >
                      {subItem.label}
                    </Link>
                  </Typography>
                ))}
              </div>
            </Collapse>
          </div>
        ) : (
          <Typography
            key={index}
            as="div"
            variant="small"
            color="blue-gray"
            className="p-1 app-font"
          >
            {item.external ? (
              <a
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center"
              >
                {item.label}
              </a>
            ) : (
              <Link
                href={item.href}
                className="flex items-center"
                onClick={(e) => handleLinkClick(e, item)}
              >
                {item.label}
              </Link>
            )}
          </Typography>
        )
      ))}
    </div>
  );

  return (
    <div className="sticky inset-0 z-50 h-max max-w-full border-none rounded-none">
      <Navbar className="sticky bg-white/80 backdrop-blur-md inset-0 z-20 h-max shadow-none drop-shadow max-w-full border-none rounded-none py-2 px-4 lg:px-5 lg:py-1.5">
        <div className="flex items-center justify-between text-blue-gray-900">
          <Link href={"/"}>
            <Typography
              as="div"
              className="mr-4 cursor-pointer py-1.5 text-xl font-bold flex gap-2 items-center"
            >
              <div className="relative w-11 h-11 lg:w-10 lg:h-10">
                <Image
                  src="/logo.png"
                  alt="Kamal Associates Logo"
                  fill
                  className="rounded-full"
                />
              </div>
              <div className="hidden lg:block">
                Kamal <span className="text-yellow-900">Associates</span>
              </div>
            </Typography>
          </Link>
          <div className="hidden lg:block">{navList}</div>

          <div className="flex items-center gap-3 lg:gap-3 ">
            <Link href={'/list-your-property'}>
              <Button
                variant=""
                className="bg-[#2e1065] border shadow-none hover:shadow-none border-violet-200 rounded-lg py-2 px-4 flex items-center space-x-2  hover:cursor-pointer"
              >
                <span className="text-white font-semibold ">Post Your Property</span>
                <span className="bg-gradient-to-r from-blue-400 to-violet-400 text-white font-semibold rounded-sm px-3 py-[1px] text-[10px] animate-pulse ">
                  Free
                </span>
              </Button>
            </Link>

            {rolePaths[user?.role] ? (
              <div className="">
                <Link
                  href={rolePaths[user.role]}
                  aria-label="Go to Dashboard"
                >
                  <FaUserCircle className="w-7 h-7" />
                </Link>
              </div>
            ) : (
              <span className="text-black hover:text-gray-900 cursor-pointer app-font">
                <LoginModal open={loginModalOpen} onOpenChange={setLoginModalOpen} />
              </span>
            )}

            <IconButton
              variant="text"
              className="lg:hidden flex justify-center items-center rounded-xl text-gray-900 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              aria-label="toogle menu"
              onClick={() => setOpenNav(!openNav)}
            >
              {openNav ? (
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              ) : (
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                </svg>
              )}
            </IconButton>
          </div>
        </div>
        <Collapse open={openNav}>
          <div className="container mx-auto">
            {mobileNavList}
          </div>
        </Collapse>
      </Navbar>
    </div>
  );
}